import { useQuery } from 'react-query';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { get, set } from '@hotjar/webapp/domains/common/app-state';
import * as Sentry from '@sentry/browser';
import type { Targeting } from '@hotjar/webapp/domains/common/app-state/types';
import { useCurrentUser } from '@hotjar/user';

export interface DismissState {
  isDismissed: boolean;
  dismiss: (silent?: boolean) => Promise<void>;
}

type AppStateCache = {
  [key in string]: string;
};

const APP_STATE_DISMISSED_VALUE = 'dismissed';
const QUERY_KEY = 'dismiss_state';

export const getDismissedState = async (
  namespace: string,
  userId?: number,
): Promise<AppStateCache | undefined> => {
  if (!userId) return undefined;
  const appState = await get(userId);

  return appState[namespace];
};

type SetIsDismissed = {
  userId: number;
  namespace: string;
  key: string;
  targeting?: Targeting;
};

const setIsDismissed = ({ userId, namespace, key, targeting }: SetIsDismissed) => {
  return set(
    userId,
    {
      namespace,
      key,
      value: APP_STATE_DISMISSED_VALUE,
    },
    targeting,
  );
};

const useDismissQuery = (namespace: string, userId?: number) => {
  return useQuery([QUERY_KEY, { userId, namespace }], () => getDismissedState(namespace, userId), {
    cacheTime: 1000 * 60 * 60 * 1, // 1h
    staleTime: 1000 * 60 * 60 * 1, // 1h
    enabled: !!userId && !!namespace,
  });
};

export const useDismiss = (namespace: string, key: string, targeting?: Targeting): DismissState => {
  const { data: currentUser } = useCurrentUser();
  const { data: dismissState } = useDismissQuery(namespace, currentUser?.id);

  const dismissedValue = dismissState && dismissState[key];

  const dismiss = async (silent = false) => {
    if (currentUser?.id) {
      try {
        await setIsDismissed({
          userId: currentUser.id,
          namespace,
          key,
          targeting,
        });

        if (!silent) {
          queryClient.invalidateQueries([QUERY_KEY, { namespace, userId: currentUser?.id }]);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  return {
    isDismissed: dismissedValue === APP_STATE_DISMISSED_VALUE,
    dismiss,
  };
};
